import React from "react";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "./RichEditor.css"; // Import custom CSS file

type RichEditorProps = {
  content: string;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  editable: boolean;
};

export default function RichEditor({
  content,
  setContent,
  editable,
}: RichEditorProps) {
  // Toolbar configuration (only used when `editable` is true)
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image", "video"],
      [{ color: [] }, { background: [] }],
      [{ "code-block": true }],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "image",
    "video",
    "code-block",
  ];

  // ✅ SECURITY: Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'blockquote',
      'ol', 'ul', 'li', 'a', 'img', 'video', 'iframe',
      'h1', 'h2', 'h3', 'span', 'pre', 'code'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });

  return (
    <div className="flex bg-white mt-1 w-full rounded-md">
      <div className="w-full min-h-48 h-full">
        {editable ? (
          // Render ReactQuill with toolbar when `editable` is true
          <ReactQuill
            style={{
              border: "none",
              padding: 0,
              backgroundColor: "transparent",
            }}
            readOnly={false}
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
          />
        ) : (
          // Render a sanitized div with the content when `editable` is false
          <div
            className="ql-editor"
            style={{
              border: "none",
              padding: 0,
              backgroundColor: "transparent",
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}
      </div>
    </div>
  );
}
